import { isEmpty, merge } from "lodash";

import { patchTech } from "@Roo/api/tech/patchTech";
import { License } from "@Roo/api/vet";
import { patchVet } from "@Roo/api/vet/patchVet";
import { useVerifyLicenseData } from "@Roo/api/vet/useVerifyLicenseData";
import { validateRequiredKeys } from "@Roo/Common/formUtils";
import { ValueOf } from "@Roo/Common/typeUtils";
import { techRegistrationFlows } from "@Roo/constants/constants";

import { Event, EventTracker } from "../../../tracking/service/EventTracker/EventTrackerService";
import { useRedirect } from "../Common/hooks/useRedirect";
import {
  RegistrationContextStateType,
  useRegistrationContext,
} from "../Common/registrationContext";
import { useIsEntity } from "../Common/service";

import { RegistrationLicenseFormType } from "./RegistrationLicenseFormContext";

export type TechRegistrationFlow = ValueOf<typeof techRegistrationFlows>;

// Utility functions
const hasLicense = (licenses: License[]) =>
  licenses.some(({ id, licenceNumber }) => id && licenceNumber);

const hasPartialLicense = (licenses: License[]) =>
  licenses.some(({ id, licenceNumber }) => (id && !licenceNumber) || (!id && licenceNumber));

const validateNoPartialLicenses = (licenses: License[]) => {
  const errors: { licenses?: string } = {};

  if (hasPartialLicense(licenses)) {
    errors["licenses"] = "Required";
  }

  return errors;
};

const validateLicenses = (licenses: License[]) => {
  const errors: { licenses?: string } = {};

  for (const license of licenses) {
    if (!license.id || !license.licenceNumber) {
      errors["licenses"] = "Required";
    }
  }

  return errors;
};

const validateVetLicenses = validateLicenses;

export const isVetLicenseValid = (licenses: License[]) => {
  const errors = validateLicenses(licenses);
  return !isEmpty(licenses) && isEmpty(errors);
};

const validateTechLicenses = ({
  licenses = [],
  isCertified,
  isLicensed,
  isRegistered,
  isVTS,
  isLicenseRequired,
}: RegistrationLicenseFormType & { isLicenseRequired?: boolean }) => {
  // If they enter a license, they must check a credential.
  const errors: Partial<Record<keyof RegistrationLicenseFormType, string>> = {};

  if (
    (hasLicense(licenses) || hasPartialLicense(licenses) || isLicenseRequired) &&
    !isCertified &&
    !isLicensed &&
    !isRegistered &&
    !isVTS
  ) {
    errors["isLicensed"] = "Please select a credential";
  }

  // In some states, a license is required.
  if (isCertified || isLicensed || isRegistered || isVTS || isLicenseRequired) {
    return { ...errors, ...validateLicenses(licenses) };
  }

  return { ...errors, ...validateNoPartialLicenses(licenses) };
};

export const isTechLicenseValid = ({
  techRegistrationFlow,
  ...rest
}: RegistrationLicenseFormType & {
  techRegistrationFlow: TechRegistrationFlow;
}) => {
  const isLicenseRequired = getIsLicenseRequired(techRegistrationFlow);
  const errors = validateTechLicenses({ ...rest, isLicenseRequired });
  return !isEmpty(rest.licenses) && isEmpty(errors);
};

export const useValidate = () => {
  const { isTech } = useIsEntity();
  const isLicenseRequired = useIsLicenseRequired();

  const validate = (form: RegistrationLicenseFormType) => {
    if (isTech) {
      return validateTechLicenses({ ...form, isLicenseRequired });
    } else {
      const requiredKeys: (keyof RegistrationLicenseFormType)[] = ["licenses"];

      return merge(
        validateRequiredKeys({ form, requiredKeys }),
        validateVetLicenses(form.licenses || [])
      );
    }
  };

  return { validate, hasLicense, hasPartialLicense };
};

const trackTechLicenseEvent = (registrationContext: { entityId: number }) =>
  EventTracker.send({
    eventName: Event.Name.TECH_CLICK_LICENSE,
    eventType: Event.Type.CLICK,
    entityType: Event.Entity.TECH,
    entityId: registrationContext.entityId,
  });

const trackVetLicenseEvents = async (registrationContext: {
  zipcode?: string;
  stateId?: number;
  entityId: number;
}) => {
  const { entityId } = registrationContext;

  await EventTracker.send({
    eventName: Event.Name.VET_CLICK_LICENSE,
    eventType: Event.Type.CLICK,
    entityType: Event.Entity.VET,
    entityId: entityId,
  });
};

const handleTechLicenseFlow = async ({
  form,
  registrationContext,
}: {
  form: RegistrationLicenseFormType;
  registrationContext: RegistrationContextStateType["registrationContext"];
}) => {
  const { userId, entityId } = registrationContext;

  await patchTech({
    userId,
    techId: entityId,
    licenses: form.licenses,
    isCertified: !!form.isCertified,
    isRegistered: !!form.isRegistered,
    isVTS: !!form.isVTS,
    isLicensed: !!form.isLicensed,
  });

  await trackTechLicenseEvent(registrationContext);
};

const handleVetLicenseFlow = async ({
  form,
  registrationContext,
}: {
  form: RegistrationLicenseFormType;
  registrationContext: RegistrationContextStateType["registrationContext"];
}) => {
  const { userId, entityId } = registrationContext;

  await patchVet({
    userId,
    vetId: entityId,
    ...form,
    deletedLicenses: undefined,
  });

  await trackVetLicenseEvents(registrationContext);
};

export const useSave = () => {
  const { redirectToEducation, redirectToWhyAreYouOnRoo } = useRedirect();
  const { registrationContext, setRegistrationContext } = useRegistrationContext();
  const { isVet, isTech } = useIsEntity();
  const { mutateAsync: verifyLicenseData } = useVerifyLicenseData();

  const save = async (form: RegistrationLicenseFormType) => {
    if (isTech) {
      await handleTechLicenseFlow({ form, registrationContext });

      setRegistrationContext({
        registrationContext: {
          ...registrationContext,
          ...form,
        },
      });

      redirectToEducation();
    } else if (isVet) {
      await handleVetLicenseFlow({ form, registrationContext });

      await verifyLicenseData({
        licenseNumber: form.licenses[0].licenceNumber?.toString() ?? "",
        userId: registrationContext.userId,
        zipcode: registrationContext.zipcode ?? "",
        state: registrationContext.stateName ?? "",
        isFromWeb: true,
      });

      setRegistrationContext({
        registrationContext: {
          ...registrationContext,
          ...form,
        },
      });

      redirectToWhyAreYouOnRoo();
    }
  };

  return { save };
};

export const getIsLicenseRequired = (techRegistrationFlow?: TechRegistrationFlow) =>
  techRegistrationFlow === techRegistrationFlows.CaliforniaFlow;

export const useIsLicenseRequired = () => {
  const {
    registrationContext: { techRegistrationFlow },
  } = useRegistrationContext();
  return getIsLicenseRequired(techRegistrationFlow);
};
